import classNames from 'classnames'
import { Dispatch, SetStateAction, useState } from 'react'
import { MyBtn } from '~/components/ui/MyBtn'
import { MyIcon } from '~/components/ui/MyIcon'
import { MyModal } from '~/components/ui/MyModal'
import { AiCreateArtworkModel } from '~/graphqls/types'
import { uniqueFunc } from '~/utils'
import { OptionsTabs } from './common/OptionsTabs'

export interface ModelThemeProps {
  name: string
  modelCode: number
}

interface ModelThemeOptionsProps {
  name: string
  url: string
  isNew: boolean | null | undefined
  modelCode: number
  category: string
  model: AiCreateArtworkModel
}

interface Props {
  current: ModelThemeProps[]
  setCurrent: Dispatch<SetStateAction<ModelThemeProps[]>>
  options: ModelThemeOptionsProps[]
  curOptions: ModelThemeOptionsProps[]
  selectModel: AiCreateArtworkModel
  setSelectModel: Dispatch<SetStateAction<AiCreateArtworkModel>>
}

/** 默认显示数量 */
const DEFAULT_SHOW_COUNT = 14

const TiledSelectItem: React.FC<{
  item: ModelThemeOptionsProps
  onChecked: () => void
  isActive: boolean
}> = ({ item, onChecked, isActive }) => {
  return (
    <div
      style={{
        background: `url(${item.url}) no-repeat center center/cover`,
        width: 'calc((100% - 48px) / 5)',
      }}
      onClick={onChecked}
      className="h-16 mb-3 rounded-lg overflow-hidden cursor-pointer relative"
    >
      {item.isNew && (
        <img
          className="h-[14px] absolute top-1 right-1"
          src="/images/ai/new.png"
          alt=""
        />
      )}
      <div
        className={classNames('h-16 rounded-lg overflow-hidden', {
          '!border-neutral-800 !border-2 !border-solid': isActive,
        })}
      >
        <div
          className="w-full h-full flex items-center justify-center"
          style={{
            background:
              'linear-gradient(180deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.5) 100%)',
          }}
        >
          <div className="text-neutral-800 font-semibold select-none">
            {item.name}
          </div>
        </div>
      </div>
    </div>
  )
}

export default function TiledSelect({
  options,
  curOptions,
  current,
  setCurrent,
  selectModel,
  setSelectModel,
}: Props) {
  const [visible, setVisible] = useState(false)
  const [, setCurrentIndex] = useState(0)

  const handleClickOption = (item: ModelThemeOptionsProps) => {
    if (current.findIndex(it => it.name === item.name) === -1) {
      setCurrent([
        {
          name: item.name,
          modelCode: item.modelCode,
        },
      ])
    }
  }

  const tabs = uniqueFunc(
    options
      ?.filter(item => !!item.category)
      ?.map(item => ({
        name: item.category,
        model: item.model,
      })),
    'model'
  )

  /** 特殊的交互，和角色同人等不同，选中的不置顶 */
  const defaultOptions = uniqueFunc(
    [
      ...curOptions?.slice(0, DEFAULT_SHOW_COUNT),
      ...current.map(item => {
        return {
          ...curOptions.find(o => o.name === item?.name),
        }
      }),
    ],
    'name'
  )

  return (
    <>
      <div className="flex flex-wrap gap-3">
        {defaultOptions.map(item => {
          const isActive = current.findIndex(it => it.name === item.name) > -1
          return (
            <TiledSelectItem
              key={item.name}
              isActive={isActive}
              item={item}
              onChecked={() => handleClickOption(item)}
            />
          )
        })}
        <div
          className={classNames(
            'h-16 mb-3 rounded-lg text-neutral-800 font-semibold border-2 border-neutral-300 cursor-pointer overflow-hidden flex items-center justify-center'
          )}
          style={{
            background: 'rgba(35, 38, 47, 0.32)',
            width: 'calc((100% - 48px) / 5)',
          }}
          onClick={() => setVisible(true)}
        >
          <MyIcon
            className="text-[14px] leading-[16px] font-normal"
            icon={'more1'}
          />
          <span className="ml-0.5">更多</span>
        </div>
      </div>
      <MyModal
        open={visible}
        title={<>模型主题</>}
        onCancel={() => setVisible(false)}
        maskStyle={{
          backgroundColor: 'rgba(0,0,0,0.8)',
        }}
        dark
        width={720}
        footer={null}
      >
        <OptionsTabs
          options={tabs.map(item => item.name)}
          currentIndex={tabs.findIndex(item => item.model === selectModel)}
          setCurrentIndex={setCurrentIndex}
          onChange={index => setSelectModel(tabs[index].model)}
        />
        <div className="flex flex-wrap gap-3">
          {curOptions.map(item => {
            const isActive = current.findIndex(it => it.name === item.name) > -1
            return (
              <TiledSelectItem
                key={item.name}
                isActive={isActive}
                item={item}
                onChecked={() => handleClickOption(item)}
              />
            )
          })}
        </div>
        <div className="flex justify-between items-center gap-3">
          <MyBtn
            block
            type="default"
            className="!bg-neutral-200 !border-neutral-200 !text-neutral-800"
            onClick={() => {
              setCurrent([])
              setVisible(false)
            }}
          >
            重置
          </MyBtn>
          <MyBtn block type="primary" onClick={() => setVisible(false)}>
            确定
          </MyBtn>
        </div>
      </MyModal>
    </>
  )
}
